import { InputWithIcon } from '@/components/ui/vehicle/input-with-icon';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleListing } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';

export type BatteryWarrantyMonthsInputProps = {
  id: string;
  value: VehicleListing['remaining_battery_warranty_months'];
  className?: string;
};

export function BatteryWarrantyMonthsInput({
  id,
  value,
  className,
}: BatteryWarrantyMonthsInputProps) {
  const updateRemainingBatteryWarrantyMonthsMutation = useMutation({
    onMutate: async ({ remainingBatteryWarrantyMonths }) => {
      vehicleListingStore.updateRemainingBatteryWarrantyMonths({
        id,
        remainingBatteryWarrantyMonths,
      });
    },
    mutationFn: async ({
      remainingBatteryWarrantyMonths,
    }: {
      remainingBatteryWarrantyMonths: VehicleListing['remaining_battery_warranty_months'];
    }) => {
      return await pdkAxios.post(
        `/inventory/listings/${id}/remaining_battery_warranty_months`,
        {
          remaining_battery_warranty_months: remainingBatteryWarrantyMonths,
        },
      );
    },
    onError: (error) => {
      toast({
        title: 'Error updating remaining battery warranty months',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleRemainingBatteryWarrantyMonthsChange = (
    newRemainingBatteryWarrantyMonths: string,
  ) => {
    if (
      newRemainingBatteryWarrantyMonths === '' ||
      isNaN(parseInt(newRemainingBatteryWarrantyMonths))
    ) {
      updateRemainingBatteryWarrantyMonthsMutation.mutate({
        remainingBatteryWarrantyMonths: 0,
      });
    } else {
      updateRemainingBatteryWarrantyMonthsMutation.mutate({
        remainingBatteryWarrantyMonths: parseInt(
          newRemainingBatteryWarrantyMonths,
        ),
      });
    }
  };

  return (
    <div>
      <InputWithIcon
        icon={<Calendar className="h-4 w-4" />}
        value={value?.toString() || ''}
        placeholder="Remaining battery warranty months"
        onChange={handleRemainingBatteryWarrantyMonthsChange}
        className={cn('pl-0', className)}
        inputClassName="w-14"
        suffix="months"
      />
    </div>
  );
}
